import { X, Check, Crown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useState } from "react"
import { GESTOCK_PLANS } from "../../config/subscriptions"

type Props = {
  isOpen: boolean
  currentPlan: string
  currentBillingCycle?: "monthly" | "yearly" | null
  onClose: () => void
  onUpgrade: (planKey: string, billingCycle: "monthly" | "yearly") => Promise<void> | void
  plans?: Record<string, any>
}

type BillingCycle = "monthly" | "yearly"

export default function SubscriptionModal({
  isOpen,
  currentPlan,
  currentBillingCycle,
  onClose,
  onUpgrade,
  plans,
}: Props) {
  const [processingPlan, setProcessingPlan] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly")

  const activePlans = withBusinessPromo(resolvePlans(plans))
  const businessPromo = (activePlans as any)?.BUSINESS?.promo
  const promoDaysLeft = getPromoDaysLeft(businessPromo?.endAt)

  const handleUpgrade = async (planKey: string) => {
    try {
      setProcessingPlan(planKey)
      await Promise.resolve(onUpgrade(planKey, billingCycle))
    } finally {
      setProcessingPlan(null)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
        <motion.div
          className="
              bg-white rounded-3xl shadow-2xl
              w-full max-w-[96vw] 2xl:max-w-[1800px]
              max-h-[92vh] overflow-y-auto
              p-5 sm:p-6 xl:p-8 space-y-6
            "
            initial={{ scale: 0.9, y: 40 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 40 }}
          >
            {/* ================= HEADER ================= */}
            <div className="flex justify-between items-start gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <Crown className="text-yellow-500 animate-pulse" />
                  Passer à un plan supérieur
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Débloquez plus de fonctionnalités pour votre boutique
                </p>
              </div>

              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition"
              >
                <X />
              </button>
            </div>

            {businessPromo?.active && (
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-lime-50 px-4 py-3">
                <p className="text-sm font-semibold text-emerald-800">
                  Offre Business en cours: {Number(businessPromo.promoPrice || 0).toLocaleString()} FCFA / mois
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  Au lieu de {Number(businessPromo.originalPrice || 0).toLocaleString()} FCFA.
                  Valable jusqu&apos;au {new Date(businessPromo.endAt).toLocaleDateString()}.
                </p>
                <p className="text-xs font-semibold text-emerald-800 mt-1.5">
                  {promoDaysLeft > 0 ? `Plus que ${promoDaysLeft} jour(s) pour en profiter.` : "Dernier jour de l'offre."}
                </p>
              </div>
            )}

            {/* ================= PLANS ================= */}
            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${billingCycle === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition ${billingCycle === "yearly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                Annuel
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
              {Object.values(activePlans).map((plan: any) => {
                const isActive =
                  plan.key === currentPlan &&
                  (plan.key === "FREE" || billingCycle === normalizeBillingCycle(currentBillingCycle))
                const isLoading = processingPlan === plan.key
                const selectedPricing = getPricingForCycle(plan, billingCycle)
                const hasPromo = billingCycle === "monthly" && Boolean(plan?.promo?.active)
                const planPrice = Number(selectedPricing?.price || plan?.price || 0)
                const promoOriginal = Number(plan?.promo?.originalPrice || planPrice || 0)
                const promoPrice = Number(plan?.promo?.promoPrice || planPrice || 0)
                const displayPrice = hasPromo ? promoPrice : planPrice

                return (
                  <div
                    key={plan.key}
                    className={`
                      relative rounded-2xl border p-5
                      flex flex-col justify-between
                      transition-all duration-300
                      ${hasPromo
                        ? "border-emerald-300 bg-gradient-to-b from-emerald-50/70 to-white shadow-md"
                        : isActive
                        ? "border-blue-600 bg-blue-50 shadow-md"
                        : "hover:border-gray-300 hover:shadow"}
                    `}
                  >
                    {hasPromo && (
                      <span className="absolute -top-3 left-4 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Promo
                      </span>
                    )}

                    {/* ACTIVE BADGE */}
                    {isActive && (
                      <span className="
                        absolute -top-3 right-4
                        bg-blue-600 text-white text-xs font-semibold
                        px-3 py-1 rounded-full
                      ">
                        Plan actuel
                      </span>
                    )}

                    {/* PLAN INFO */}
                    <div>
                      <h3 className="text-lg font-semibold">
                        {plan.name}
                      </h3>

                      <p className="text-2xl font-bold mt-2">
                        {displayPrice === 0
                          ? "Gratuit"
                          : `${displayPrice.toLocaleString()} FCFA`}
                        {displayPrice !== 0 && (
                          <span className="text-sm font-normal text-gray-500">
                            {" "} / {billingCycle === "yearly" ? "an" : "mois"}
                          </span>
                        )}
                      </p>
                      {hasPromo && (
                        <div className="mt-1 space-y-0.5">
                          <p className="text-xs text-gray-500 line-through">
                            {promoOriginal.toLocaleString()} FCFA / mois
                          </p>
                          <p className="text-sm font-semibold text-emerald-700">
                            Prix promo: {promoPrice.toLocaleString()} FCFA / mois
                          </p>
                        </div>
                      )}
                      {billingCycle === "yearly" && displayPrice > 0 && (
                        <p className="mt-1 text-xs font-medium text-slate-500">
                          Engagement 12 mois
                        </p>
                      )}

                      <ul className="mt-4 space-y-2 text-sm">
                        {Object.entries(plan.features)
                          .filter(([key]) => key !== "MOBILE_MONEY")
                          .map(([key, enabled]) => (
                          <li key={key} className="flex items-center gap-2">
                            <Check
                              size={16}
                              className={
                                enabled
                                  ? "text-green-600"
                                  : "text-gray-300"
                              }
                            />
                            <span
                              className={
                                enabled
                                  ? "text-gray-800"
                                  : "text-gray-500 line-through"
                              }
                            >
                              {formatFeature(key)}
                            </span>
                          </li>
                        ))}
                        <li className="flex items-center gap-2">
                          <Check
                            size={16}
                            className="text-green-600"
                          />
                          <span className="text-gray-800">
                            {getProductLimitLabel(plan)}
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check
                            size={16}
                            className={plan?.limits?.sms?.canSend ? "text-green-600" : "text-gray-300"}
                          />
                          <span
                            className={
                              plan?.limits?.sms?.canSend
                                ? "text-gray-800"
                                : "text-gray-600"
                            }
                          >
                            {getSmsFeatureLabel(plan)}
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* CTA */}
                    <button
                      disabled={isActive || !!processingPlan}
                      onClick={() => handleUpgrade(plan.key)}
                      className={`
                        mt-6 w-full py-2.5 rounded-xl font-semibold
                        transition
                        ${isActive || !!processingPlan
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : hasPromo
                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                            : "bg-blue-600 text-white hover:bg-blue-700"}
                      `}
                    >
                      {isActive
                        ? "Plan actuel"
                        : isLoading
                          ? "Chargement..."
                          : hasPromo
                            ? `Choisir à ${promoPrice.toLocaleString()} FCFA`
                            : `Choisir ${billingCycle === "yearly" ? "annuel" : "mensuel"}`}
                    </button>
                  </div>
                )
              })}
            </div>

            {/* ================= FOOTER ================= */}
            <p className="text-xs text-gray-600 text-center">
              Vous pouvez changer ou annuler votre abonnement à tout moment.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function resolvePlans(plans?: Record<string, any>) {
  if (!plans || Object.keys(plans).length === 0) return GESTOCK_PLANS

  return Object.fromEntries(
    Object.entries(GESTOCK_PLANS).map(([key, fallbackPlan]) => {
      const remotePlan = plans?.[key] || {}

      return [
        key,
        {
          ...fallbackPlan,
          ...remotePlan,
          limits: {
            ...(fallbackPlan?.limits || {}),
            ...(remotePlan?.limits || {}),
            sms: {
              ...(fallbackPlan?.limits?.sms || {}),
              ...(remotePlan?.limits?.sms || {}),
            },
          },
          features: {
            ...(fallbackPlan?.features || {}),
            ...(remotePlan?.features || {}),
          },
          promo: remotePlan?.promo,
        },
      ]
    })
  )
}

function normalizeBillingCycle(cycle?: string | null): BillingCycle {
  return String(cycle || "").toLowerCase() === "yearly" ? "yearly" : "monthly"
}

function getPricingForCycle(plan: any, billingCycle: BillingCycle) {
  const pricing = plan?.pricing || {}
  return pricing[billingCycle] || pricing.monthly || { price: Number(plan?.price || 0) }
}

function withBusinessPromo(plans: Record<string, any>) {
  const safePlans = { ...(plans || {}) }
  const business = safePlans.BUSINESS
  if (!business) return safePlans

  if (business?.promo?.active) return safePlans

  const fallbackPromo = {
    active: isWithinPromoWindow(PROMO_START_AT, PROMO_END_AT),
    originalPrice: 25900,
    promoPrice: 16900,
    startAt: PROMO_START_AT,
    endAt: PROMO_END_AT,
    label: "Promo BUSINESS - 1 mois",
  }

  if (!fallbackPromo.active) return safePlans

  safePlans.BUSINESS = {
    ...business,
    price: fallbackPromo.promoPrice,
    promo: fallbackPromo,
  }

  return safePlans
}

function isWithinPromoWindow(startAt: string, endAt: string) {
  const now = new Date()
  const start = new Date(startAt)
  const end = new Date(endAt)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  return now >= start && now <= end
}

function getPromoDaysLeft(endAt?: string) {
  if (!endAt) return 0
  const end = new Date(endAt)
  if (Number.isNaN(end.getTime())) return 0
  const diffMs = end.getTime() - Date.now()
  if (diffMs <= 0) return 0
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

const PROMO_START_AT = "2026-03-09T00:00:00.000Z"
const PROMO_END_AT = "2026-04-09T23:59:59.999Z"

/* ================= FEATURE LABEL ================= */

function formatFeature(key: string) {
  switch (key) {
    case "MOBILE_MONEY":
      return "Paiement Mobile Money"
    case "ECOMMERCE":
      return "Site e-commerce personnalisé"
    case "MULTI_STORE":
      return "Gestion multi-boutiques"
    case "ADVANCED_REPORTS":
      return "Rapports avancés"
    default:
      return key
  }
}

function getSmsFeatureLabel(plan: any) {
  const sms = plan?.limits?.sms

  if (sms) {
    if (!sms.canSend) return "Campagnes SMS (sans envoi)"
    return `${Number(sms.monthlyIncluded || 0)} SMS inclus/mois, puis ${Number(sms.overagePrice || 0)} FCFA/SMS`
  }

  // Fallback for plan payloads that don't include limits.sms
  const key = String(plan?.key || "").toUpperCase()
  if (key === "FREE") return "Campagnes SMS (sans envoi)"
  if (key === "LITE") return "20 SMS inclus/mois, puis 14 FCFA/SMS"
  if (key === "STANDARD") return "50 SMS inclus/mois, puis 13 FCFA/SMS"
  if (key === "PRO") return "100 SMS inclus/mois, puis 12 FCFA/SMS"
  if (key === "BUSINESS") return "300 SMS inclus/mois, puis 10 FCFA/SMS"
  return "Campagnes SMS"
}

function getProductLimitLabel(plan: any) {
  const maxProducts = plan?.limits?.maxProducts

  if (maxProducts === null || Number(maxProducts) <= 0) {
    return "Produits illimites"
  }

  return `Jusqu'a ${Number(maxProducts).toLocaleString()} produits`
}
